import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { settingsApi } from '../../services/api';

const C = {
  card:'#ffffff', border:'#f0e4d8', border2:'#f5ede4',
  text1:'#1a0f00', text3:'#6b4c35', text4:'#a07858',
  accent:'#c96428', accentL:'#fff4ee', accentB:'#f0d0b0',
  surface:'#fffaf7', green:'#16a34a', greenL:'#dcfce7',
};

const inp = {
  width:'100%', padding:'11px 14px', borderRadius:10, fontSize:14,
  border:`1.5px solid ${C.border}`, background:C.surface,
  color:C.text1, fontFamily:'inherit', outline:'none',
  transition:'border-color 0.15s, box-shadow 0.15s', boxSizing:'border-box',
};

const focusStyle = e => { e.target.style.borderColor=C.accent; e.target.style.boxShadow=`0 0 0 3px rgba(201,100,40,0.1)`; };
const blurStyle  = e => { e.target.style.borderColor=C.border; e.target.style.boxShadow='none'; };

const Label = ({ children }) => (
  <label style={{ display:'block', color:C.text3, fontSize:11, fontWeight:700, marginBottom:7, textTransform:'uppercase', letterSpacing:0.5 }}>
    {children}
  </label>
);

const Section = ({ icon, title, subtitle, children, accent }) => (
  <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}
    style={{ background:C.card, borderRadius:18, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px', background: accent ? `linear-gradient(135deg,${C.accentL},#fff)` : `linear-gradient(135deg,#fdfaf7,#fff)`, borderBottom:`1px solid ${C.border2}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:14 }}>
        <div style={{ width:44, height:44, borderRadius:13, background: accent ? C.accentL : '#f5ede4', border:`1.5px solid ${accent ? C.accentB : C.border}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{icon}</div>
        <div>
          <p style={{ fontFamily:'"Playfair Display",serif', fontSize:16, fontWeight:700, color:C.text1, margin:0 }}>{title}</p>
          <p style={{ color:C.text4, fontSize:12, margin:'3px 0 0' }}>{subtitle}</p>
        </div>
      </div>
    </div>
    <div style={{ padding:24 }}>{children}</div>
  </motion.div>
);

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    cafe_name:'', cafe_address:'', cafe_phone:'', cafe_email:'',
    contact_address:'123 Coffee Lane, Brew District, NY 10001',
    contact_phone:'+1 (555) 123-4567',
    contact_email:'hello@aromacafe.com',
    contact_hours:'Mon–Fri 7am–10pm · Sat–Sun 8am–11pm',
    gst_enabled:'true', gst_rate:'5', gst_number:'', gst_label:'GST',
    delivery_enabled:'false',
    delivery_charge:'0',
    footer_description:'',
    social_instagram:'',
    social_facebook:'',
    social_twitter:'',
    opening_hours_mon_fri:'7:00 AM – 10:00 PM',
    opening_hours_sat:'8:00 AM – 11:00 PM',
    opening_hours_sun:'8:00 AM – 11:00 PM',
    max_guests:'50',
    time_slots:'01:00,01:30,02:00,02:30,03:00,03:30,04:00,04:30,05:00,05:30,06:00,06:30,07:00,07:30,08:00,08:30,09:00,09:30,10:00,10:30,11:00,11:30,12:00,12:30,13:00,13:30,14:00,14:30,15:00,15:30,16:00,16:30,17:00,17:30,18:00,18:30,19:00,19:30,20:00,20:30,21:00,21:30,22:00,22:30,23:00,23:30,24:00',
    reservation_policy:'Reservations held for 15 minutes||Groups of 8+ require a deposit||Cancellations within 2 hours may incur a fee||Special accommodations available on request',
    // Branding / Homepage
    navbar_name:'', navbar_logo_url:'',
    hero_bg_url:'', hero_title:'', hero_title2:'', hero_subtitle:'',
    // Hero status badge
    hero_status_text:'Now Open',
    hero_status_hours:'Mon–Fri 7am–10pm',
    // Stats section
    stat_customers:'50K+',
    stat_menu_items:'120+',
    stat_years:'8',
    stat_awards:'24',
    // About page
    about_hero_url:'',
    about_established:'2016',
    about_heading:'A Cup of Passion, A Heart of Community',
    about_story1:'SwadZone was born from a simple dream: to create a space where the aroma of freshly brewed coffee blends with the warmth of genuine human connection.',
    about_story2:'Every bean is sourced directly from sustainable farms. Every recipe is crafted with intention. Every visit is treated as an opportunity to create a memory.',
    about_cafe_img_url:'',
    team1_name:'', team1_role:'', team1_bio:'', team1_photo_url:'',
    team2_name:'', team2_role:'', team2_bio:'', team2_photo_url:'',
    team3_name:'', team3_role:'', team3_bio:'', team3_photo_url:'',
  });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [uploadingHero,      setUploadingHero]      = useState(false);
  const [uploadingLogo,      setUploadingLogo]      = useState(false);
  const [uploadingAboutHero, setUploadingAboutHero] = useState(false);
  const [uploadingCafeImg,   setUploadingCafeImg]   = useState(false);
  const [uploadingTeam,      setUploadingTeam]      = useState([false,false,false]);
  const heroFileRef      = useRef();
  const logoFileRef      = useRef();
  const aboutHeroFileRef = useRef();
  const cafeImgFileRef   = useRef();
  const teamFileRefs     = [useRef(), useRef(), useRef()];

  useEffect(() => {
    settingsApi.get()
      .then(r => { if (r.data.data) setSettings(p => ({ ...p, ...r.data.data })); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await settingsApi.update(settings); toast.success('Settings saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleImageUpload = async (key, file, setUploading) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await settingsApi.uploadImage(key, file);
      const url = res.data.data.url;
      set(key, url);
      toast.success('Image uploaded!');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const gstOn  = settings.gst_enabled === 'true';
  const gstRate = parseFloat(settings.gst_rate) || 0;
  const deliveryOn = settings.delivery_enabled === 'true';

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {Array(4).fill(0).map((_,i) => <div key={i} style={{ height:120, borderRadius:18, background:'#f5ede4', animation:'pulse 1.5s infinite' }}/>)}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.55}}`}</style>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Page header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h1 style={{ fontFamily:'"Playfair Display",serif', fontSize:26, fontWeight:800, color:C.text1, margin:0 }}>Settings</h1>
          <p style={{ color:C.text4, fontSize:13, marginTop:4 }}>Manage your cafe information and configuration</p>
        </div>
        <motion.button whileTap={{scale:0.97}} type="button" onClick={handleSave} disabled={saving}
          style={{ padding:'12px 28px', borderRadius:12, border:'none', fontSize:14, fontWeight:700, fontFamily:'inherit', cursor:saving?'not-allowed':'pointer', background:saving?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', boxShadow:saving?'none':'0 4px 16px rgba(201,100,40,0.35)', display:'flex', alignItems:'center', gap:8 }}>
          {saving ? '⏳ Saving...' : '💾 Save All Settings'}
        </motion.button>
      </div>

      <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:20 }}>

        {/* ── Homepage & Branding ── */}
        <Section icon="🎨" title="Homepage & Branding" subtitle="Customize your navbar name, logo, and hero section" accent>

          {/* Navbar Name */}
          <div style={{ marginBottom:20 }}>
            <Label>Restaurant Display Name (Navbar)</Label>
            <p style={{ color:C.text4, fontSize:12, marginBottom:8 }}>Shown in the top navigation bar. First word normal, rest will appear in accent color.</p>
            <input type="text" placeholder="SwadZone"
              value={settings.navbar_name||''}
              onChange={e => set('navbar_name', e.target.value)}
              style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            {/* Preview */}
            <div style={{ marginTop:10, padding:'10px 16px', borderRadius:10, background:'#f8f4f0', border:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:11, color:C.text4, marginRight:4 }}>Preview:</span>
              {(() => {
                const nm = (settings.navbar_name||'SwadZone').trim().split(' ');
                return (
                  <span style={{ fontFamily:'"Playfair Display",serif', fontSize:16, fontWeight:600, color:'#1a0f00' }}>
                    {nm[0]}{nm.length > 1 ? <> <span style={{ color:C.accent }}>{nm.slice(1).join(' ')}</span></> : ''}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Logo Upload */}
          <div style={{ marginBottom:20, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <div>
              <Label>Navbar Logo Image</Label>
              <p style={{ color:C.text4, fontSize:12, marginBottom:8 }}>Square image recommended (PNG/JPG). If not set, first letter of name is used.</p>
              <input ref={logoFileRef} type="file" accept="image/*" style={{ display:'none' }}
                onChange={e => handleImageUpload('navbar_logo_url', e.target.files[0], setUploadingLogo)}/>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {settings.navbar_logo_url && (
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <img src={settings.navbar_logo_url} alt="Logo preview"
                      style={{ width:48, height:48, borderRadius:10, objectFit:'cover', border:`1.5px solid ${C.border}` }}/>
                    <button type="button" onClick={() => set('navbar_logo_url', '')}
                      style={{ padding:'4px 10px', borderRadius:7, border:`1px solid #fecaca`, background:'#fff5f5', color:'#ef4444', cursor:'pointer', fontSize:12 }}>
                      Remove
                    </button>
                  </div>
                )}
                <button type="button" onClick={() => logoFileRef.current?.click()} disabled={uploadingLogo}
                  style={{ padding:'9px 16px', borderRadius:10, border:`1.5px dashed ${C.accent}`, background:C.accentL, color:C.accent, cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                  {uploadingLogo ? '⏳ Uploading...' : '📁 Upload Logo'}
                </button>
              </div>
            </div>

            {/* Logo Preview */}
            <div>
              <Label>Navbar Preview</Label>
              <div style={{ padding:'12px 16px', borderRadius:12, background:'white', border:`1.5px solid ${C.border}`, display:'flex', alignItems:'center', gap:10, marginTop:2 }}>
                {settings.navbar_logo_url ? (
                  <img src={settings.navbar_logo_url} alt="logo" style={{ width:36, height:36, borderRadius:8, objectFit:'cover' }}/>
                ) : (
                  <div style={{ width:36, height:36, borderRadius:8, background:C.accent, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:16 }}>
                    {(settings.navbar_name||'SwadZone').trim().charAt(0).toUpperCase()}
                  </div>
                )}
                <span style={{ fontFamily:'"Playfair Display",serif', fontSize:15, fontWeight:600, color:'#1a0f00' }}>
                  {(() => {
                    const nm = (settings.navbar_name||'SwadZone').trim().split(' ');
                    return <>{nm[0]}{nm.length > 1 ? <> <span style={{ color:C.accent }}>{nm.slice(1).join(' ')}</span></> : ''}</>;
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* Hero Background Image */}
          <div style={{ marginBottom:20 }}>
            <Label>Hero Background Image</Label>
            <p style={{ color:C.text4, fontSize:12, marginBottom:8 }}>Full-width image shown behind the homepage hero text. Landscape photo recommended (1600×900+).</p>
            <input ref={heroFileRef} type="file" accept="image/*" style={{ display:'none' }}
              onChange={e => handleImageUpload('hero_bg_url', e.target.files[0], setUploadingHero)}/>
            {settings.hero_bg_url ? (
              <div style={{ position:'relative', borderRadius:14, overflow:'hidden', marginBottom:10, border:`1.5px solid ${C.border}` }}>
                <img src={settings.hero_bg_url} alt="Hero preview"
                  style={{ width:'100%', height:160, objectFit:'cover', display:'block' }}/>
                <div style={{ position:'absolute', top:8, right:8, display:'flex', gap:6 }}>
                  <button type="button" onClick={() => heroFileRef.current?.click()} disabled={uploadingHero}
                    style={{ padding:'6px 12px', borderRadius:8, border:'none', background:'rgba(0,0,0,0.6)', color:'white', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                    {uploadingHero ? '⏳' : '🔄 Change'}
                  </button>
                  <button type="button" onClick={() => set('hero_bg_url', '')}
                    style={{ padding:'6px 12px', borderRadius:8, border:'none', background:'rgba(239,68,68,0.8)', color:'white', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                    ✕ Remove
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => heroFileRef.current?.click()} disabled={uploadingHero}
                style={{ width:'100%', padding:'24px', borderRadius:12, border:`2px dashed ${C.accent}`, background:C.accentL, color:C.accent, cursor:'pointer', fontSize:14, fontWeight:600, fontFamily:'inherit', display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:28 }}>🖼️</span>
                {uploadingHero ? 'Uploading...' : 'Click to Upload Hero Background'}
                <span style={{ fontSize:11, color:C.text4, fontWeight:400 }}>JPG, PNG, WebP — max 5MB</span>
              </button>
            )}
          </div>

          {/* Hero Text */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:8 }}>
            <div>
              <Label>Hero Title (Line 1 — White)</Label>
              <input type="text" placeholder="Where Every Cup"
                value={settings.hero_title||''}
                onChange={e => set('hero_title', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Hero Title (Line 2 — Accent / Italic)</Label>
              <input type="text" placeholder="Tells a Story"
                value={settings.hero_title2||''}
                onChange={e => set('hero_title2', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div style={{ gridColumn:'1 / -1' }}>
              <Label>Hero Subtitle / Tagline</Label>
              <textarea rows={2} placeholder="Handcrafted coffee, artisanal pastries..."
                value={settings.hero_subtitle||''}
                onChange={e => set('hero_subtitle', e.target.value)}
                style={{ ...inp, resize:'none', lineHeight:1.5 }} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
          </div>

          {/* Hero Text Preview */}
          {(settings.hero_title || settings.hero_title2 || settings.hero_subtitle) && (
            <div style={{ borderRadius:12, overflow:'hidden', border:`1px solid ${C.border}` }}>
              <div style={{ padding:'8px 14px', background:C.accentL, fontSize:11, fontWeight:700, color:C.accent, textTransform:'uppercase', letterSpacing:0.5 }}>
                Hero Text Preview
              </div>
              <div style={{ padding:'20px 24px', background:'#1a0f00', position:'relative' }}>
                <p style={{ fontFamily:'"Playfair Display",serif', fontSize:24, fontWeight:800, color:'white', margin:'0 0 4px', lineHeight:1.2 }}>
                  {settings.hero_title || 'Where Every Cup'}
                </p>
                <p style={{ fontFamily:'"Playfair Display",serif', fontSize:24, fontWeight:700, color:C.accent, fontStyle:'italic', margin:'0 0 10px', lineHeight:1.2 }}>
                  {settings.hero_title2 || 'Tells a Story'}
                </p>
                <p style={{ fontSize:13, color:'rgba(255,255,255,0.7)', margin:0, lineHeight:1.5, maxWidth:400 }}>
                  {settings.hero_subtitle || 'Handcrafted coffee, artisanal pastries, and a warm ambiance...'}
                </p>
              </div>
            </div>
          )}
          {/* Hero Status Badge */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:16 }}>
            <div>
              <Label>Hero Status Text</Label>
              <input type="text" placeholder="Now Open"
                value={settings.hero_status_text||''}
                onChange={e => set('hero_status_text', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
              <p style={{ color:C.text4, fontSize:11, marginTop:4 }}>e.g. "Now Open" — shown as badge on hero</p>
            </div>
            <div>
              <Label>Hero Status Hours</Label>
              <input type="text" placeholder="Mon–Fri 7am–10pm"
                value={settings.hero_status_hours||''}
                onChange={e => set('hero_status_hours', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
              <p style={{ color:C.text4, fontSize:11, marginTop:4 }}>e.g. "Mon–Fri 7am–10pm · Sat–Sun 8am–11pm"</p>
            </div>
          </div>
        </Section>

        {/* ── Stats Section ── */}
        <Section icon="📊" title="Homepage Stats" subtitle="Numbers shown in the stats bar below the hero section" accent>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <div>
              <Label>Happy Customers</Label>
              <input type="text" placeholder="50K+"
                value={settings.stat_customers||''}
                onChange={e => set('stat_customers', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Menu Items</Label>
              <input type="text" placeholder="120+"
                value={settings.stat_menu_items||''}
                onChange={e => set('stat_menu_items', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Years of Excellence</Label>
              <input type="text" placeholder="8"
                value={settings.stat_years||''}
                onChange={e => set('stat_years', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Awards Won</Label>
              <input type="text" placeholder="24"
                value={settings.stat_awards||''}
                onChange={e => set('stat_awards', e.target.value)}
                style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
          </div>
          <div style={{ marginTop:16, padding:'12px 16px', borderRadius:10, background:C.accentL, border:`1px solid ${C.border}`, fontSize:12, color:C.text3 }}>
            💡 Numbers shown as-is — use formats like <strong>50K+</strong>, <strong>120+</strong>, <strong>8</strong>, <strong>24</strong>. Count-up animation runs automatically on the homepage.
          </div>
        </Section>

        {/* ── GST / Tax ── */}
        <Section icon="🧾" title="GST / Tax Settings" subtitle="Configure tax shown on customer bills and receipts" accent>
          {/* Toggle */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, padding:'14px 18px', borderRadius:12, background: gstOn ? C.greenL : '#f5f5f5', border:`1px solid ${gstOn ? '#a5d6a7' : C.border}` }}>
            <div>
              <p style={{ fontWeight:700, color: gstOn ? C.green : C.text3, margin:0, fontSize:14 }}>{gstOn ? '✅ Tax Enabled' : '⭕ Tax Disabled'}</p>
              <p style={{ color:C.text4, fontSize:12, margin:'2px 0 0' }}>Toggle to enable/disable tax on all orders</p>
            </div>
            <div onClick={() => set('gst_enabled', gstOn ? 'false' : 'true')}
              style={{ width:52, height:28, borderRadius:99, cursor:'pointer', position:'relative', background:gstOn?C.green:C.border, transition:'all 0.25s', boxShadow:gstOn?'0 2px 8px rgba(22,163,74,0.4)':'none' }}>
              <div style={{ position:'absolute', top:4, width:20, height:20, background:'white', borderRadius:'50%', transition:'transform 0.25s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transform:gstOn?'translateX(26px)':'translateX(4px)' }}/>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div>
              <Label>GST Rate (%)</Label>
              <div style={{ position:'relative' }}>
                <input type="number" min="0" max="100" step="0.1"
                  value={settings.gst_rate} disabled={!gstOn}
                  onChange={e => set('gst_rate', e.target.value)}
                  style={{ ...inp, paddingRight:40, opacity:gstOn?1:0.5 }}
                  onFocus={focusStyle} onBlur={blurStyle}/>
                <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', color:C.accent, fontWeight:800, fontSize:14 }}>%</span>
              </div>
            </div>
            <div>
              <Label>Tax Label</Label>
              <select value={settings.gst_label} disabled={!gstOn}
                onChange={e => set('gst_label', e.target.value)}
                style={{ ...inp, opacity:gstOn?1:0.5 }}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="GST">GST</option>
                <option value="CGST + SGST">CGST + SGST</option>
                <option value="IGST">IGST</option>
                <option value="VAT">VAT</option>
                <option value="Service Tax">Service Tax</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <Label>GSTIN Number</Label>
            <input type="text" placeholder="e.g. 27AABCU9603R1ZX"
              value={settings.gst_number||''} disabled={!gstOn}
              onChange={e => set('gst_number', e.target.value)}
              style={{ ...inp, fontFamily:'monospace', letterSpacing:1, opacity:gstOn?1:0.5 }}
              onFocus={focusStyle} onBlur={blurStyle}/>
            <p style={{ color:C.text4, fontSize:11, marginTop:5 }}>Printed on customer receipt/bill</p>
          </div>

          {/* Bill preview */}
          {gstOn && gstRate > 0 && (
            <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}}
              style={{ borderRadius:12, overflow:'hidden', border:`1px solid ${C.accentB}`, marginBottom:16 }}>
              <div style={{ padding:'10px 16px', background:C.accentL, display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:14 }}>📋</span>
                <span style={{ color:C.accent, fontSize:12, fontWeight:700 }}>Bill Preview (on ₹1,000 order)</span>
              </div>
              <div style={{ padding:'14px 16px', background:'#fffdf9' }}>
                {[['Subtotal','₹1,000',C.text3],[`${settings.gst_label} (${gstRate}%)`,`₹${(1000*gstRate/100).toFixed(0)}`,C.accent]].map(([l,v,c]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:8 }}>
                    <span style={{ color:C.text4 }}>{l}</span>
                    <span style={{ color:c, fontWeight:600 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:16, fontWeight:800, paddingTop:10, borderTop:`2px solid ${C.accent}`, marginTop:4 }}>
                  <span style={{ color:C.text1 }}>Total Payable</span>
                  <span style={{ color:C.accent }}>₹{(1000+1000*gstRate/100).toFixed(0)}</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Quick presets */}
          <div>
            <p style={{ color:C.text4, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:10 }}>Quick Presets</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {[{label:'Restaurant (5%)',rate:'5'},{label:'Cafe (12%)',rate:'12'},{label:'Fine Dining (18%)',rate:'18'},{label:'No Tax (0%)',rate:'0'}].map(p => (
                <button key={p.label} type="button"
                  onClick={() => setSettings(s => ({ ...s, gst_rate:p.rate, gst_enabled:p.rate==='0'?'false':'true' }))}
                  style={{ padding:'8px 16px', borderRadius:99, fontSize:12, fontWeight:600, fontFamily:'inherit', cursor:'pointer', transition:'all 0.15s',
                    background:settings.gst_rate===p.rate?C.accentL:'#fff',
                    color:settings.gst_rate===p.rate?C.accent:C.text3,
                    border:`1.5px solid ${settings.gst_rate===p.rate?C.accentB:C.border}`,
                    boxShadow:settings.gst_rate===p.rate?`0 2px 8px rgba(201,100,40,0.15)`:'none',
                  }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* ── Delivery Toggle ── */}
        <Section icon="🛵" title="Delivery Orders" subtitle="Enable or disable home delivery option for customers" accent>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', borderRadius:12, background: deliveryOn ? C.greenL : '#f5f5f5', border:`1px solid ${deliveryOn ? '#a5d6a7' : C.border}` }}>
            <div>
              <p style={{ fontWeight:700, color: deliveryOn ? C.green : C.text3, margin:0, fontSize:14 }}>
                {deliveryOn ? '✅ Delivery Enabled — Customers can choose Delivery' : '⭕ Delivery Disabled — Delivery option hidden from customers'}
              </p>
              <p style={{ color:C.text4, fontSize:12, margin:'2px 0 0' }}>
                ON करें तो Order Type में 🛵 Delivery का option दिखेगा, OFF करें तो hide हो जाएगा
              </p>
            </div>
            <div onClick={() => set('delivery_enabled', deliveryOn ? 'false' : 'true')}
              style={{ width:52, height:28, borderRadius:99, cursor:'pointer', position:'relative', background:deliveryOn?C.green:C.border, transition:'all 0.25s', flexShrink:0, marginLeft:16, boxShadow:deliveryOn?'0 2px 8px rgba(22,163,74,0.4)':'none' }}>
              <div style={{ position:'absolute', top:4, width:20, height:20, background:'white', borderRadius:'50%', transition:'transform 0.25s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transform:deliveryOn?'translateX(26px)':'translateX(4px)' }}/>
            </div>
          </div>
          {deliveryOn && (
            <div style={{ marginTop:16, padding:'16px 18px', borderRadius:12, background:'#e8f5e9', border:'1px solid #a5d6a7' }}>
              <p style={{ fontSize:13, color:'#2e7d32', margin:'0 0 12px' }}>🟢 Delivery is <strong>active</strong> — customers will see the Delivery option in Order Type dropdown.</p>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ fontWeight:700, fontSize:13, color:'#2e7d32', whiteSpace:'nowrap' }}>🛵 Delivery Charge (₹):</label>
                <input
                  type="number"
                  min="0"
                  value={settings.delivery_charge || '0'}
                  onChange={e => set('delivery_charge', e.target.value)}
                  style={{ width:120, padding:'8px 12px', border:'1.5px solid #a5d6a7', borderRadius:8, fontSize:14, fontFamily:'inherit', outline:'none', background:'white' }}
                  placeholder="0"
                />
                <span style={{ fontSize:12, color:'#2e7d32' }}>0 = Free Delivery</span>
              </div>
            </div>
          )}
          {!deliveryOn && (
            <div style={{ marginTop:12, padding:'10px 16px', borderRadius:10, background:'#fafafa', border:'1px solid #e0e0e0', fontSize:13, color:'#9e9e9e' }}>
              ⭕ Delivery is <strong>off</strong> — customers will only see Dine In and Takeaway options.
            </div>
          )}
        </Section>

        {/* ── Cafe Info ── */}
        <Section icon="🏪" title="Cafe Information" subtitle="Shown on receipts and website">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {[
              {key:'cafe_name',label:'Cafe Name',ph:'SwadZone'},
              {key:'cafe_phone',label:'Phone',ph:'+91 98765 43210'},
              {key:'cafe_email',label:'Email',ph:'hello@aromacafe.in'},
            ].map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <input type="text" placeholder={f.ph} value={settings[f.key]||''}
                  onChange={e => set(f.key, e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
              </div>
            ))}
            <div style={{ gridColumn:'1 / -1' }}>
              <Label>Address</Label>
              <textarea rows={2} placeholder="42, MG Road, Bengaluru..."
                value={settings.cafe_address||''}
                onChange={e => set('cafe_address', e.target.value)}
                style={{ ...inp, resize:'none', lineHeight:1.5 }} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
          </div>
        </Section>

        {/* ── Opening Hours ── */}
        <Section icon="🕐" title="Opening Hours" subtitle="Displayed on the reservation page">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            {[
              {key:'opening_hours_mon_fri',label:'Mon – Fri',ph:'7:00 AM – 10:00 PM'},
              {key:'opening_hours_sat',label:'Saturday',ph:'8:00 AM – 11:00 PM'},
              {key:'opening_hours_sun',label:'Sunday',ph:'8:00 AM – 11:00 PM'},
            ].map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <input type="text" placeholder={f.ph} value={settings[f.key]||''}
                  onChange={e => set(f.key, e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
              </div>
            ))}
          </div>
          <div style={{marginTop:16}}>
            <Label>Map Location Search</Label>
            <p style={{fontSize:12,color:'#a07858',marginBottom:6}}>Restaurant ka address type karo — map automatically show hoga</p>
            <div style={{display:'flex',gap:8}}>
              <input type="text" placeholder="e.g. SwadZone, MG Road, Ahmedabad"
                value={settings.map_search_query||''}
                onChange={e => set('map_search_query', e.target.value)}
                style={{...inp, flex:1}} onFocus={focusStyle} onBlur={blurStyle}/>
              <button type="button"
                onClick={() => {
                  if (settings.map_search_query?.trim()) {
                    set('map_embed_url', 'https://maps.google.com/maps?q=' + encodeURIComponent(settings.map_search_query.trim()) + '&output=embed&hl=en');
                  }
                }}
                style={{padding:'0 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#c96428,#9a4a1e)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>
                🔍 Search
              </button>
            </div>
            {settings.map_embed_url && (
              <div style={{marginTop:12,borderRadius:12,overflow:'hidden',height:220,border:'1px solid #f0e4d8'}}>
                <iframe src={settings.map_embed_url} width="100%" height="220" style={{border:0}} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Map Preview"/>
              </div>
            )}
          </div>
        </Section>

        {/* ── Reservation Options ── */}
        <Section icon="🪑" title="Reservation Options" subtitle="Control guest count and time slots shown to customers">
          <div style={{ marginBottom:24 }}>
            <Label>Maximum Guests per Booking</Label>
            <p style={{ color:C.text4, fontSize:12, marginBottom:12 }}>Customers can book for 1 up to this number of guests. Quick select ya custom value enter karo</p>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:12 }}>
              {[10,20,30,50,100,200].map(n => {
                const active = settings.max_guests === String(n);
                return (
                  <button key={n} type="button" onClick={() => set('max_guests', String(n))}
                    style={{ minWidth:52, height:52, padding:'0 12px', borderRadius:12, fontSize:15, fontWeight:800, fontFamily:'inherit', cursor:'pointer', transition:'all 0.15s',
                      background:active?'linear-gradient(135deg,#c96428,#9a4a1e)':'#fff',
                      color:active?'#fff':C.text3,
                      border:`1.5px solid ${active?C.accent:C.border}`,
                      boxShadow:active?'0 4px 12px rgba(201,100,40,0.3)':'none',
                    }}>{n}</button>
                );
              })}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input
                type="number" min="1" max="9999"
                value={settings.max_guests}
                onChange={e => set('max_guests', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle}
                placeholder="Custom number..."
                style={{ ...inp, width:160 }}
              />
              <span style={{ fontSize:13, color:C.text4 }}>guests (custom value)</span>
            </div>
          </div>

          <div>
            <Label>Available Time Slots</Label>
            <p style={{ color:C.text4, fontSize:12, marginBottom:8 }}>Click to toggle — <span style={{ color:C.green, fontWeight:600 }}>Green = active</span>, Grey = disabled</p>
            <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
              <button type="button" onClick={() => {
                const ALL = [];
                for(let h=1;h<=24;h++){
                  ALL.push(`${String(h).padStart(2,'0')}:00`);
                  if(h<24) ALL.push(`${String(h).padStart(2,'0')}:30`);
                }
                ALL.push('24:00');
                set('time_slots', [...new Set(ALL)].sort().join(','));
              }} style={{ padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', background:'#e8f5e9', color:C.green, border:'1.5px solid #a5d6a7', fontFamily:'inherit' }}>
                ✅ Select All
              </button>
              <button type="button" onClick={() => set('time_slots', '')}
                style={{ padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', background:'#fafafa', color:C.text4, border:`1.5px solid ${C.border}`, fontFamily:'inherit' }}>
                ❌ Clear All
              </button>
            </div>
            {(() => {
              const ALL_SLOTS = [];
              for(let h=1;h<=24;h++){
                ALL_SLOTS.push(`${String(h).padStart(2,'0')}:00`);
                if(h<24) ALL_SLOTS.push(`${String(h).padStart(2,'0')}:30`);
              }
              ALL_SLOTS.push('24:00');
              const active = (settings.time_slots||'').split(',').filter(Boolean);
              const toggle = slot => {
                const cur = (settings.time_slots||'').split(',').filter(Boolean);
                const next = cur.includes(slot)?cur.filter(s=>s!==slot):[...cur,slot].sort();
                set('time_slots', next.join(','));
              };
              // Group by hour for better layout
              const hours = [];
              for(let h=1;h<=24;h++) hours.push(h);
              return (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {hours.map(h => {
                    const hh = String(h).padStart(2,'0');
                    const slots = h<24 ? [`${hh}:00`,`${hh}:30`] : ['24:00'];
                    return (
                      <div key={h} style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:11, color:C.text4, fontFamily:'monospace', width:32, flexShrink:0 }}>{hh}h</span>
                        {slots.map(slot => {
                          const on = active.includes(slot);
                          return (
                            <button key={slot} type="button" onClick={() => toggle(slot)}
                              style={{ padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:700, fontFamily:'monospace', cursor:'pointer', transition:'all 0.15s',
                                background:on?C.greenL:'#fff', color:on?C.green:C.text4,
                                border:`1.5px solid ${on?'#a5d6a7':C.border}`,
                                boxShadow:on?'0 2px 6px rgba(22,163,74,0.15)':'none',
                              }}>{slot}</button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </Section>

        {/* ── Reservation Policy ── */}
        <Section icon="📋" title="Reservation Policy" subtitle="Policy points shown on the reservation page">
          {(() => {
            const points = (settings.reservation_policy||'').split('||');
            const update = (i,val) => { const p=[...points]; p[i]=val; set('reservation_policy',p.join('||')); };
            const remove = i => set('reservation_policy', points.filter((_,idx)=>idx!==i).join('||'));
            const add    = () => set('reservation_policy', (settings.reservation_policy||'')+'||New policy point');
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {points.map((pt,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:C.accentL, border:`1px solid ${C.accentB}`, display:'flex', alignItems:'center', justifyContent:'center', color:C.accent, fontWeight:800, fontSize:14, flexShrink:0 }}>•</div>
                    <input type="text" value={pt} onChange={e=>update(i,e.target.value)}
                      style={{ ...inp, flex:1 }} onFocus={focusStyle} onBlur={blurStyle}/>
                    <button type="button" onClick={()=>remove(i)}
                      style={{ width:34, height:34, borderRadius:9, border:`1px solid #fecaca`, background:'#fff5f5', color:'#ef4444', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>✕</button>
                  </div>
                ))}
                <button type="button" onClick={add}
                  style={{ alignSelf:'flex-start', padding:'9px 18px', borderRadius:10, fontSize:13, fontWeight:700, fontFamily:'inherit', cursor:'pointer', background:C.accentL, color:C.accent, border:`1px solid ${C.accentB}`, marginTop:4 }}>
                  + Add Policy Point
                </button>
              </div>
            );
          })()}
        </Section>

        {/* ── Contact Info ── */}
        <Section icon="📍" title="Contact Information" subtitle="Shown on the Contact page">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {[
              {key:'contact_address',label:'Address',ph:'123 Coffee Lane, NY 10001'},
              {key:'contact_phone',label:'Phone',ph:'+1 (555) 123-4567'},
              {key:'contact_email',label:'Email',ph:'hello@aromacafe.com'},
              {key:'contact_hours',label:'Opening Hours',ph:'Mon–Fri 7am–10pm'},
            ].map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <input type="text" placeholder={f.ph} value={settings[f.key]||''}
                  onChange={e => set(f.key, e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
              </div>
            ))}
          </div>
        </Section>


        {/* ── About Page ── */}
        <Section icon="📖" title="About Page" subtitle="Customize your story, team members, and about page images" accent>

          {/* About Hero Image */}
          <div style={{marginBottom:24}}>
            <Label>About Hero Background Image</Label>
            <input ref={aboutHeroFileRef} type="file" accept="image/*" style={{display:'none'}}
              onChange={e => handleImageUpload('about_hero_url', e.target.files[0], setUploadingAboutHero)}/>
            {settings.about_hero_url ? (
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
                <img src={settings.about_hero_url} alt="About Hero"
                  style={{width:100,height:60,objectFit:'cover',borderRadius:8,border:`1px solid ${C.border}`}}/>
                <div style={{display:'flex',gap:8}}>
                  <button type="button" onClick={() => aboutHeroFileRef.current?.click()} disabled={uploadingAboutHero}
                    style={{padding:'7px 14px',borderRadius:8,border:`1.5px solid ${C.border}`,background:C.surface,color:C.text3,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                    {uploadingAboutHero ? '⏳' : '🔄 Change'}
                  </button>
                  <button type="button" onClick={() => set('about_hero_url','')}
                    style={{padding:'7px 14px',borderRadius:8,border:'1.5px solid #fee2e2',background:'#fff5f5',color:'#b91c1c',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                    🗑️ Remove
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => aboutHeroFileRef.current?.click()} disabled={uploadingAboutHero}
                style={{padding:'12px 20px',borderRadius:10,border:`1.5px dashed ${C.accentB}`,background:C.accentL,color:C.accent,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',width:'100%'}}>
                {uploadingAboutHero ? '⏳ Uploading...' : '📁 Upload About Hero Image (1400×500+ recommended)'}
              </button>
            )}
          </div>

          {/* Established + Heading */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:16,marginBottom:16}}>
            <div>
              <Label>Established Year</Label>
              <input type="text" placeholder="2016" value={settings.about_established||''}
                onChange={e => set('about_established', e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Section Heading</Label>
              <input type="text" placeholder="A Cup of Passion, A Heart of Community" value={settings.about_heading||''}
                onChange={e => set('about_heading', e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
          </div>

          {/* Story paragraphs */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
            {[
              {key:'about_story1',label:'Story Paragraph 1'},
              {key:'about_story2',label:'Story Paragraph 2'},
            ].map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <textarea rows={4} placeholder="Write your cafe story..." value={settings[f.key]||''}
                  onChange={e => set(f.key, e.target.value)}
                  style={{...inp, resize:'vertical', lineHeight:1.6}}
                  onFocus={focusStyle} onBlur={blurStyle}/>
              </div>
            ))}
          </div>

          {/* Cafe image */}
          <div style={{marginBottom:28}}>
            <Label>Cafe Interior / Story Image</Label>
            <input ref={cafeImgFileRef} type="file" accept="image/*" style={{display:'none'}}
              onChange={e => handleImageUpload('about_cafe_img_url', e.target.files[0], setUploadingCafeImg)}/>
            {settings.about_cafe_img_url ? (
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <img src={settings.about_cafe_img_url} alt="Cafe"
                  style={{width:100,height:60,objectFit:'cover',borderRadius:8,border:`1px solid ${C.border}`}}/>
                <div style={{display:'flex',gap:8}}>
                  <button type="button" onClick={() => cafeImgFileRef.current?.click()} disabled={uploadingCafeImg}
                    style={{padding:'7px 14px',borderRadius:8,border:`1.5px solid ${C.border}`,background:C.surface,color:C.text3,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                    {uploadingCafeImg ? '⏳' : '🔄 Change'}
                  </button>
                  <button type="button" onClick={() => set('about_cafe_img_url','')}
                    style={{padding:'7px 14px',borderRadius:8,border:'1.5px solid #fee2e2',background:'#fff5f5',color:'#b91c1c',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                    🗑️ Remove
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => cafeImgFileRef.current?.click()} disabled={uploadingCafeImg}
                style={{padding:'12px 20px',borderRadius:10,border:`1.5px dashed ${C.accentB}`,background:C.accentL,color:C.accent,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',width:'100%'}}>
                {uploadingCafeImg ? '⏳ Uploading...' : '📁 Upload Cafe Interior Image (800×600+ recommended)'}
              </button>
            )}
          </div>

          {/* Team members */}
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <p style={{fontFamily:'"Playfair Display",serif',fontSize:15,fontWeight:700,color:C.text1}}>👥 Team Members</p>
              <span style={{fontSize:11,color:C.text4,background:C.accentL,border:`1px solid ${C.accentB}`,borderRadius:6,padding:'4px 10px'}}>
                💡 Naam khali chhodo = wo member nahi dikhega
              </span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:20}}>
              {[1,2,3].map(n => {
                const photoKey = `team${n}_photo_url`;
                const isUploading = uploadingTeam[n-1];
                const setUploading = (v) => setUploadingTeam(prev => prev.map((x,i)=>i===n-1?v:x));
                return (
                  <div key={n} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:16}}>
                    <p style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:12,textTransform:'uppercase',letterSpacing:0.5}}>
                      Member {n}
                    </p>
                    <div style={{marginBottom:12,textAlign:'center'}}>
                      <input ref={teamFileRefs[n-1]} type="file" accept="image/*" style={{display:'none'}}
                        onChange={e => handleImageUpload(photoKey, e.target.files[0], setUploading)}/>
                      {settings[photoKey] ? (
                        <div>
                          <img src={settings[photoKey]} alt={`Team ${n}`}
                            style={{width:72,height:72,borderRadius:'50%',objectFit:'cover',border:`2px solid ${C.accentB}`,display:'block',margin:'0 auto 8px'}}/>
                          <button type="button" onClick={() => teamFileRefs[n-1].current?.click()} disabled={isUploading}
                            style={{fontSize:11,padding:'4px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:C.card,color:C.text3,cursor:'pointer',fontFamily:'inherit'}}>
                            {isUploading ? '⏳' : '🔄 Change Photo'}
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => teamFileRefs[n-1].current?.click()} disabled={isUploading}
                          style={{width:72,height:72,borderRadius:'50%',border:`2px dashed ${C.accentB}`,background:C.accentL,color:C.accent,fontSize:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 8px'}}>
                          {isUploading ? '⏳' : '📷'}
                        </button>
                      )}
                    </div>
                    {[
                      {key:`team${n}_name`,label:'Name',ph:'Marco Rossi'},
                      {key:`team${n}_role`,label:'Role',ph:'Head Barista'},
                      {key:`team${n}_bio`, label:'Bio', ph:'15 years of coffee mastery...'},
                    ].map(f => (
                      <div key={f.key} style={{marginBottom:10}}>
                        <Label>{f.label}</Label>
                        {f.key.includes('bio') ? (
                          <textarea rows={3} placeholder={f.ph} value={settings[f.key]||''}
                            onChange={e => set(f.key, e.target.value)}
                            style={{...inp,resize:'vertical',fontSize:12}} onFocus={focusStyle} onBlur={blurStyle}/>
                        ) : (
                          <input type="text" placeholder={f.ph} value={settings[f.key]||''}
                            onChange={e => set(f.key, e.target.value)} style={{...inp,fontSize:12}} onFocus={focusStyle} onBlur={blurStyle}/>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        {/* ── Footer & Social ── */}
        <Section icon="🌐" title="Footer & Social Media" subtitle="Footer description and social media links">
          <div style={{ marginBottom: 16 }}>
            <Label>Footer Description</Label>
            <textarea rows={3} placeholder="Where every cup tells a story..." value={settings.footer_description||''}
              onChange={e => set('footer_description', e.target.value)}
              style={{...inp, resize:'vertical', lineHeight:1.6}} onFocus={focusStyle} onBlur={blurStyle}/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            <div>
              <Label>Instagram URL</Label>
              <input type="text" placeholder="https://instagram.com/..." value={settings.social_instagram||''}
                onChange={e => set('social_instagram', e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Facebook URL</Label>
              <input type="text" placeholder="https://facebook.com/..." value={settings.social_facebook||''}
                onChange={e => set('social_facebook', e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
            <div>
              <Label>Twitter/X URL</Label>
              <input type="text" placeholder="https://twitter.com/..." value={settings.social_twitter||''}
                onChange={e => set('social_twitter', e.target.value)} style={inp} onFocus={focusStyle} onBlur={blurStyle}/>
            </div>
          </div>
        </Section>

        {/* ── Save button bottom ── */}
        <div style={{ display:'flex', justifyContent:'flex-end', paddingBottom:16 }}>
          <motion.button whileTap={{scale:0.97}} type="submit" disabled={saving}
            style={{ padding:'14px 36px', borderRadius:13, border:'none', fontSize:15, fontWeight:700, fontFamily:'inherit', cursor:saving?'not-allowed':'pointer', background:saving?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', boxShadow:saving?'none':'0 4px 18px rgba(201,100,40,0.35)', display:'flex', alignItems:'center', gap:10 }}>
            {saving ? '⏳ Saving...' : '💾 Save All Settings'}
          </motion.button>
        </div>

      </form>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.55}}`}</style>
    </div>
  );
};

export default AdminSettings;
